import { useState, useEffect, useRef } from 'react';
import { BarChart3, TrendingUp, DollarSign, Users, Play } from 'lucide-react';

export function TrackAnalytics({ track, tokenInfo, royalties = [], onPlayCountUpdate }) {
  const [animatedPlays, setAnimatedPlays] = useState(track?.plays || 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevPlaysRef = useRef(track?.plays || 0);
  
  // Calculate total revenue from royalties
  const totalRoyalties = royalties.reduce((sum, r) => sum + (r.total_amount || 0), 0);
  
  // Ownership stats
  const tokensSold = 100 - (tokenInfo?.available_tokens || 100);
  const tokenHolders = tokenInfo?.owners_count || 0;

  // Animate play count when it changes
  useEffect(() => {
    const targetPlays = track?.plays || 0;
    
    if (targetPlays !== prevPlaysRef.current) {
      setIsAnimating(true);
      const startValue = prevPlaysRef.current;
      const diff = targetPlays - startValue;
      const duration = 1000; // 1 second animation
      const steps = 30;
      const stepDuration = duration / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        // Ease out cubic
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.round(startValue + (diff * easeProgress));
        setAnimatedPlays(currentValue);

        if (currentStep >= steps) {
          clearInterval(timer);
          setAnimatedPlays(targetPlays);
          setIsAnimating(false);
          prevPlaysRef.current = targetPlays;
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [track?.plays]);

  return (
    <div className="glass-card p-6 mb-6" data-testid="track-analytics">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Track Analytics</h2>
          <p className="text-sm text-gray-400">Performance & earnings data</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <AnimatedStatCard
          icon={<Play className="w-5 h-5 text-purple-400" />}
          label="Total Plays"
          value={animatedPlays}
          isAnimating={isAnimating}
          formatter={formatNumber}
        />
        <StatCard
          icon={<DollarSign className="w-5 h-5 text-green-400" />}
          label="Revenue Generated"
          value={`$${totalRoyalties.toFixed(2)}`}
          trend={totalRoyalties > 0 ? '+' : null}
        />
        <StatCard
          icon={<Users className="w-5 h-5 text-cyan-400" />}
          label="Token Holders"
          value={tokenHolders}
          trend={null}
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5 text-yellow-400" />}
          label="Ownership Sold"
          value={`${tokensSold}%`}
          trend={tokensSold > 50 ? 'hot' : null}
        />
      </div>

      {/* Royalty History */}
      {royalties.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-3">Recent Royalty Distributions</h3>
          <div className="space-y-2">
            {royalties.slice(0, 3).map((r) => (
              <div key={r.id || `${r.period_start}-${r.period_end}`} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-sm font-medium">
                    {formatDateRange(r.period_start, r.period_end)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {r.distributions?.length || 0} holders paid
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-400">${r.total_amount?.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">total distributed</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {royalties.length === 0 && (
        <div className="text-center py-6 bg-white/5 rounded-lg">
          <DollarSign className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No royalty distributions yet</p>
          <p className="text-xs text-gray-500 mt-1">Earnings will be shown here once distributed</p>
        </div>
      )}
    </div>
  );
}

function AnimatedStatCard({ icon, label, value, isAnimating, formatter }) {
  return (
    <div className={`bg-white/5 rounded-lg p-4 transition-all duration-300 ${isAnimating ? 'ring-2 ring-purple-500/50' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        {icon}
        {isAnimating && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className={`text-xl font-bold transition-all duration-150 ${isAnimating ? 'text-purple-400 scale-110' : ''}`}>
        {formatter ? formatter(value) : value}
      </p>
    </div>
  );
}

function StatCard({ icon, label, value, trend }) {
  return (
    <div className="bg-white/5 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        {icon}
        {trend === '+' && <span className="text-xs text-green-400">↑</span>}
        {trend === 'hot' && <span className="text-xs text-orange-400">🔥</span>}
      </div>
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

function formatDateRange(start, end) {
  if (!start || !end) return 'Unknown period';
  const startDate = new Date(start);
  const endDate = new Date(end);
  const options = { month: 'short', day: 'numeric' };
  return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
}
